/*
  B2B PAGE CHROME — the retreating header, and smooth in-page navigation.

  Naoya, 2026-08-30: "when i scroll up, the nav bar would show up instantly i kinda dont like
  it, it feels too cheap… Also when i click the page link to move to that section, it's also
  instant to go to that page, which feels too cheap as well."

  ── THE HEADER ────────────────────────────────────────────────────────────────────────────

  The first version reappeared on the FIRST PIXEL of upward movement, past a 6px threshold
  that only existed to stop trackpad jitter. That is what reads as cheap: the bar tracking
  every twitch of the hand rather than responding to an intention. The standard implementation
  — the one behind every header that feels considered — accumulates movement in one direction
  and only acts once the reader has clearly committed to it.

      hide after   80px of continuous downward scroll
      reveal after 48px of continuous upward scroll
      accumulator resets the moment direction changes

  So a small correction does nothing at all, and a real "take me back up" brings the bar in.

  ⚠️ The CSS transition that should have softened this was silently dead. `transition: transform
  260ms var(--shintop-ease)` sat on the SECTION WRAPPER, and `--shintop-ease` is declared inside
  core's `.shintop` scope — which the wrapper does not carry, because `.shintop` is on the
  `<header>` inside it. An undefined `var()` invalidates the whole shorthand, so the computed
  `transition-duration` was **0s** and every movement was a hard cut. Measured, not guessed. The
  stylesheet now uses a literal curve on this element for exactly that reason.

  ── THE ANCHORS ───────────────────────────────────────────────────────────────────────────

  The scroll is ANIMATED BY HAND, frame by frame — not with `scroll-behavior: smooth` and not
  with `window.scrollTo({ behavior: 'smooth' })`. Both were tried and neither can be trusted:
  measured on this page, `scrollTo({ top: 1200, behavior: 'auto' })` lands on 1200 while
  `behavior: 'smooth'` leaves scrollY at **0** — through `window` and through
  `document.scrollingElement` alike, with reduced-motion off. Native smooth scrolling is simply
  absent in some environments, and a navigation that silently does nothing is far worse than one
  that is not animated.

  ⚠️ That also corrects the record. `scroll-behavior: smooth` was removed from this page on
  2026-08-30 with the conclusion that it "broke hash navigation" — the same measurement, and the
  same cause. The CSS was not at fault; smooth scrolling was unavailable where it was tested.

  A hand-written tween has three advantages beyond working: the duration scales with the
  distance travelled, the curve is ours to choose, and the animation yields the moment the
  reader touches the wheel or a key, so it can never fight them.

  Three things the naive version gets wrong and this does not:

  1. THE OFFSET. A sticky bar covers whatever a raw anchor jump lands on, so the target is
     computed minus the bar's height and a breathing gap.
  2. THE HASH. Setting `location.hash` makes the browser jump on top of the animation, so the
     address bar is updated with `history.pushState` — still copyable, Back still works.
  3. FOCUS. A sighted reader's eye follows the scroll; a keyboard user's focus does not, and
     would carry on from the link they just left. The target takes focus when the movement
     finishes, with `preventScroll` so focusing cannot undo it.

  Anyone who has asked for reduced motion gets the instant jump, on both behaviours.
*/
(() => {
  /*
    S03-11 (audit 2026-09-13, chunk 9). THE THEME EDITOR RE-EXECUTES THIS FILE.

    This is a plain script, not a custom element, so it has no `disconnectedCallback` and
    nothing tears it down. When a section is edited, Shopify re-renders it and re-injects its
    markup INCLUDING its <script> tags, and the browser runs them again. Everything below then
    bound a second time: a second `scroll` listener, and — the one that shows — a second
    `document` click delegate, so a single click on an in-page anchor started two competing
    rAF tweens down the same page. Measured in the audit: one anchor click animates twice.
    Editor-only; it is never a shopper. But it is Naoya and Jam who use the editor, and a
    header that behaves oddly while you are editing it is a header you distrust afterwards.

    Two halves:

      · THE DOCUMENT- AND WINDOW-LEVEL HANDLERS BIND ONCE. The first execution parks its
        re-mount function on `window`; a later execution finds it, re-mounts, and returns
        before binding anything.
      · THE BAR IS RE-RESOLVED, NOT CAPTURED. After a re-render the original node is detached,
        so a captured `const bar` would leave the retreating header driving a node that is no
        longer on the page. `bar` is now reassigned by `mount()` on every
        `shopify:section:load`, and `update()` checks `isConnected` so a section DELETED and
        never replaced simply stops rather than throwing.
  */
  const MOUNT_KEY = 'shintopB2bHeaderMount';
  if (window[MOUNT_KEY]) {
    window[MOUNT_KEY]();
    return;
  }

  let bar = document.querySelector('.shintop-b2b-header-section');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- the retreating header ---------- */

  {
    const HIDDEN = 'shintop-b2b-header-section--hidden';
    const STUCK = 'shintop-b2b-header-section--stuck';
    const HIDE_AFTER = 80;
    const SHOW_AFTER = 48;

    let last = window.scrollY;
    let travelled = 0;
    let queued = false;

    const update = () => {
      queued = false;
      if (!bar || !bar.isConnected) return;
      const y = window.scrollY;
      const moved = y - last;
      last = y;

      bar.classList.toggle(STUCK, y > 4);

      // Above the bar's own height there is nothing to occlude, and a bar that
      // flickers away on the first pixel of movement reads as a fault.
      if (y <= bar.offsetHeight) {
        bar.classList.remove(HIDDEN);
        travelled = 0;
        return;
      }

      // Never slide out from under a keyboard user who is inside it.
      if (bar.contains(document.activeElement)) {
        bar.classList.remove(HIDDEN);
        travelled = 0;
        return;
      }

      // A change of direction starts the count again — that is what makes a small
      // correction do nothing and a deliberate move do something.
      if ((moved > 0 && travelled < 0) || (moved < 0 && travelled > 0)) travelled = 0;
      travelled += moved;

      if (travelled > HIDE_AFTER) {
        bar.classList.add(HIDDEN);
        travelled = 0;
      } else if (travelled < -SHOW_AFTER) {
        bar.classList.remove(HIDDEN);
        travelled = 0;
      }
    };

    window.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    const onFocusIn = () => {
      if (bar) bar.classList.remove(HIDDEN);
    };

    // Re-resolve the bar and start its counters over. The `focusin` listener is per-node, so
    // it is taken off the outgoing one before the reference moves — the node is usually gone
    // by now, but "usually" is not a teardown.
    const mount = () => {
      if (bar) bar.removeEventListener('focusin', onFocusIn);
      bar = document.querySelector('.shintop-b2b-header-section');
      if (!bar) return;
      bar.addEventListener('focusin', onFocusIn);
      last = window.scrollY;
      travelled = 0;
      update();
    };

    document.addEventListener('shopify:section:load', mount);
    window[MOUNT_KEY] = mount;
    mount();
  }

  /* ---------- smooth in-page navigation ---------- */

  const gap = 16;
  let animation = null;

  const settle = (target) => {
    // A heading is not focusable, so it is made programmatically focusable for this one
    // purpose; `preventScroll` stops focusing from undoing the animation.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  // easeOutCubic — leaves immediately, arrives gently.
  //
  // easeInOutCubic was tried first and measured wrong for this job: sampling the real scroll
  // showed it barely moving for the first ~400ms of a 900ms journey, because a symmetric curve
  // is slow at BOTH ends. On a scroll the reader started by clicking, that reads as lag — the
  // opposite complaint to the one being fixed. A click should be answered at once and the
  // landing is what wants softening.
  const ease = (t) => 1 - Math.pow(1 - t, 3);

  const glide = (to, onDone) => {
    const from = window.scrollY;
    const distance = to - from;
    if (Math.abs(distance) < 2) return onDone();

    // Long journeys take longer, but within bounds — a 6,000px page should not take six seconds.
    const duration = Math.min(900, Math.max(420, Math.abs(distance) * 0.35));
    const started = performance.now();
    let cancelled = false;

    const stop = () => {
      cancelled = true;
    };
    // The reader always wins: any real input abandons the animation where it stands.
    const opts = { passive: true, once: true };
    window.addEventListener('wheel', stop, opts);
    window.addEventListener('touchstart', stop, opts);
    window.addEventListener('keydown', stop, opts);

    const step = (now) => {
      if (cancelled) return;
      const t = Math.min(1, (now - started) / duration);
      window.scrollTo(0, Math.round(from + distance * ease(t)));
      if (t < 1) {
        animation = requestAnimationFrame(step);
      } else {
        onDone();
      }
    };
    animation = requestAnimationFrame(step);
  };
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const id = link.getAttribute('href').slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();

    if (animation) cancelAnimationFrame(animation);

    const offset = bar && !bar.classList.contains('shintop-b2b-header-section--hidden')
      ? bar.offsetHeight
      : 0;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const top = Math.min(
      max,
      Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset - gap)
    );

    history.pushState(null, '', `#${id}`);

    if (reduced.matches) {
      window.scrollTo(0, top);
      settle(target);
      return;
    }

    glide(top, () => settle(target));
  });
})();
